import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Search,
  Users,
  Phone,
  Video,
  MoreVertical,
  Paperclip,
  Smile,
  Pin,
  Archive,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/shared/utils';
import { NewConversationDialog } from './NewConversationDialog';
import { NewConversationRequest } from '@/types/contacts';

interface Message {
  id: string;
  content: string;
  senderId: string;
  recipientId?: string;
  conversationId: string;
  timestamp: string;
  type: 'text' | 'image' | 'file';
  attachments?: Attachment[];
  isRead: boolean;
  isEdited?: boolean;
  replyTo?: string;
}

interface Conversation {
  id: string;
  type: 'direct' | 'group';
  name?: string;
  participants: User[];
  lastMessage?: Message;
  unreadCount: number;
  isPinned: boolean;
  isArchived: boolean;
  avatarUrl?: string;
  description?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  unitNumber: string;
  avatarUrl?: string;
  isOnline: boolean;
  lastSeen?: string;
  role: 'resident' | 'board_member' | 'admin' | 'staff';
}

interface Attachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

// Mock data
const mockUsers: User[] = [
  {
    id: '1',
    firstName: 'Sarah',
    lastName: 'Johnson',
    unitNumber: 'A-301',
    avatarUrl: '/placeholder.svg',
    isOnline: true,
    role: 'board_member',
  },
  {
    id: '2',
    firstName: 'Michael',
    lastName: 'Chen',
    unitNumber: 'B-205',
    avatarUrl: '/placeholder.svg',
    isOnline: false,
    lastSeen: '2024-01-24T14:30:00Z',
    role: 'board_member',
  },
  {
    id: '3',
    firstName: 'Emma',
    lastName: 'Wilson',
    unitNumber: 'C-102',
    avatarUrl: '/placeholder.svg',
    isOnline: true,
    role: 'resident',
  },
  {
    id: '4',
    firstName: 'David',
    lastName: 'Brown',
    unitNumber: 'D-205',
    avatarUrl: '/placeholder.svg',
    isOnline: false,
    lastSeen: '2024-01-24T09:15:00Z',
    role: 'board_member',
  },
];

const mockConversations: Conversation[] = [
  {
    id: '1',
    type: 'direct',
    participants: [mockUsers[0], mockUsers[2]], // Current user (0) and Emma
    unreadCount: 2,
    isPinned: true,
    isArchived: false,
    lastMessage: {
      id: 'msg1',
      content: 'Thanks for organizing the pool party!',
      senderId: '3',
      conversationId: '1',
      timestamp: '2024-01-24T15:30:00Z',
      type: 'text',
      isRead: false,
    },
  },
  {
    id: '2',
    type: 'group',
    name: 'Board Members',
    description: 'Private board member communications',
    participants: [mockUsers[0], mockUsers[1], mockUsers[3]],
    unreadCount: 5,
    isPinned: false,
    isArchived: false,
    avatarUrl: '/placeholder.svg',
    lastMessage: {
      id: 'msg2',
      content: 'Meeting agenda has been updated',
      senderId: '2',
      conversationId: '2',
      timestamp: '2024-01-24T12:45:00Z',
      type: 'text',
      isRead: false,
    },
  },
  {
    id: '3',
    type: 'direct',
    participants: [mockUsers[0], mockUsers[1]], // Current user and Michael
    unreadCount: 0,
    isPinned: false,
    isArchived: false,
    lastMessage: {
      id: 'msg3',
      content: 'Budget report is ready for review',
      senderId: '1',
      conversationId: '3',
      timestamp: '2024-01-24T10:00:00Z',
      type: 'text',
      isRead: true,
    },
  },
];

const mockMessages: { [conversationId: string]: Message[] } = {
  '1': [
    {
      id: 'msg1-1',
      content: 'Hey Emma! How are you doing?',
      senderId: '1',
      conversationId: '1',
      timestamp: '2024-01-24T14:00:00Z',
      type: 'text',
      isRead: true,
    },
    {
      id: 'msg1-2',
      content:
        "Hi Sarah! I'm great, thanks for asking. Really excited about the upcoming pool party!",
      senderId: '3',
      conversationId: '1',
      timestamp: '2024-01-24T14:05:00Z',
      type: 'text',
      isRead: true,
    },
    {
      id: 'msg1-3',
      content:
        "That's wonderful! We have about 20 people signed up so far. Can you help with decorations?",
      senderId: '1',
      conversationId: '1',
      timestamp: '2024-01-24T14:10:00Z',
      type: 'text',
      isRead: true,
    },
    {
      id: 'msg1-4',
      content: 'Absolutely! I can bring some pool floats and banners. What time should I arrive?',
      senderId: '3',
      conversationId: '1',
      timestamp: '2024-01-24T15:25:00Z',
      type: 'text',
      isRead: false,
    },
    {
      id: 'msg1-5',
      content: 'Thanks for organizing the pool party!',
      senderId: '3',
      conversationId: '1',
      timestamp: '2024-01-24T15:30:00Z',
      type: 'text',
      isRead: false,
    },
  ],
  '2': [
    {
      id: 'msg2-1',
      content: "Good morning everyone. I've updated the agenda for tomorrow's meeting.",
      senderId: '2',
      conversationId: '2',
      timestamp: '2024-01-24T09:00:00Z',
      type: 'text',
      isRead: true,
    },
    {
      id: 'msg2-2',
      content: 'Thanks Michael. Can you share the financial projections we discussed?',
      senderId: '1',
      conversationId: '2',
      timestamp: '2024-01-24T09:15:00Z',
      type: 'text',
      isRead: true,
    },
    {
      id: 'msg2-3',
      content: "I'll have the full report ready by this afternoon.",
      senderId: '4',
      conversationId: '2',
      timestamp: '2024-01-24T11:30:00Z',
      type: 'text',
      isRead: false,
    },
    {
      id: 'msg2-4',
      content: "Perfect. Also, don't forget we need to discuss the pool renovation proposal.",
      senderId: '2',
      conversationId: '2',
      timestamp: '2024-01-24T12:00:00Z',
      type: 'text',
      isRead: false,
    },
    {
      id: 'msg2-5',
      content: 'Meeting agenda has been updated',
      senderId: '2',
      conversationId: '2',
      timestamp: '2024-01-24T12:45:00Z',
      type: 'text',
      isRead: false,
    },
  ],
};

interface MessagingSystemProps {
  className?: string;
  currentUserId?: string;
}

export function MessagingSystem({ className, currentUserId = '1' }: MessagingSystemProps) {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  const getConversationName = (conversation: Conversation): string => {
    if (conversation.type === 'group') {
      return conversation.name || 'Group Chat';
    }

    const otherParticipant = conversation.participants.find((p) => p.id !== currentUserId);
    return otherParticipant
      ? `${otherParticipant.firstName} ${otherParticipant.lastName}`
      : 'Unknown';
  };

  const getConversationAvatar = (conversation: Conversation): string | undefined => {
    if (conversation.type === 'group') {
      return conversation.avatarUrl;
    }

    const otherParticipant = conversation.participants.find((p) => p.id !== currentUserId);
    return otherParticipant?.avatarUrl;
  };

  const getConversationSubtitle = (conversation: Conversation): string => {
    if (conversation.type === 'group') {
      return `${conversation.participants.length} members`;
    }

    const otherParticipant = conversation.participants.find((p) => p.id !== currentUserId);
    if (!otherParticipant) return '';

    if (otherParticipant.isOnline) {
      return 'Online';
    } else if (otherParticipant.lastSeen) {
      const lastSeen = new Date(otherParticipant.lastSeen);
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60 * 60));

      if (diffInHours < 1) {
        return 'Last seen recently';
      } else if (diffInHours < 24) {
        return `Last seen ${diffInHours}h ago`;
      } else {
        return `Last seen ${Math.floor(diffInHours / 24)}d ago`;
      }
    }

    return 'Unit ' + otherParticipant.unitNumber;
  };

  const handleSendMessage = () => {
    if (!messageInput.trim() || !selectedConversation) return;

    // In a real implementation, this would send the message via API
    console.log('Sending message:', messageInput, 'to conversation:', selectedConversation);
    setMessageInput('');
  };

  const handleNewChatCreate = (conversation: NewConversationRequest) => {
    console.log('Creating new chat:', conversation);
    // In a real implementation, this would:
    // 1. Call API to create the conversation
    // 2. Add the new conversation to the list
    // 3. Select the new conversation
    // 4. Close the dialog
    setShowNewChatDialog(false);
  };

  const filteredConversations = mockConversations.filter((conversation) => {
    if (selectedTab === 'pinned') return conversation.isPinned;
    if (selectedTab === 'archived') return conversation.isArchived;
    if (selectedTab === 'unread') return conversation.unreadCount > 0;

    if (searchQuery) {
      const name = getConversationName(conversation).toLowerCase();
      return name.includes(searchQuery.toLowerCase());
    }

    return !conversation.isArchived;
  });

  const selectedConversationData = mockConversations.find((c) => c.id === selectedConversation);
  const conversationMessages = selectedConversation ? mockMessages[selectedConversation] || [] : [];

  return (
    <div
      className={cn('h-[700px] flex border border-border rounded-lg overflow-hidden', className)}
    >
      {/* Conversation List */}
      <div className="w-1/3 border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Messages</h3>
            <NewConversationDialog
              trigger={
                <Button size="sm" variant="outline">
                  New Chat
                </Button>
              }
              open={showNewChatDialog}
              onOpenChange={setShowNewChatDialog}
              onConversationCreate={handleNewChatCreate}
              defaultType="chat"
            />
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all" className="text-xs">
                All
              </TabsTrigger>
              <TabsTrigger value="unread" className="text-xs">
                Unread
              </TabsTrigger>
              <TabsTrigger value="pinned" className="text-xs">
                Pinned
              </TabsTrigger>
              <TabsTrigger value="archived" className="text-xs">
                Archived
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => setSelectedConversation(conversation.id)}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors',
                  selectedConversation === conversation.id && 'bg-muted',
                )}
              >
                <div className="relative">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={getConversationAvatar(conversation)} />
                    <AvatarFallback>
                      {conversation.type === 'group'
                        ? conversation.name?.slice(0, 2).toUpperCase()
                        : getConversationName(conversation)
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                    </AvatarFallback>
                  </Avatar>
                  {conversation.type === 'direct' && (
                    <div
                      className={cn(
                        'absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-background',
                        conversation.participants.find((p) => p.id !== currentUserId)?.isOnline
                          ? 'bg-green-500'
                          : 'bg-gray-400',
                      )}
                    />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">
                        {getConversationName(conversation)}
                      </span>
                      {conversation.isPinned && <Pin className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <div className="flex items-center gap-1">
                      {conversation.lastMessage && (
                        <span className="text-xs text-muted-foreground">
                          {formatTime(conversation.lastMessage.timestamp)}
                        </span>
                      )}
                      {conversation.unreadCount > 0 && (
                        <Badge variant="default" className="h-5 w-5 p-0 text-xs">
                          {conversation.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground truncate">
                      {conversation.lastMessage?.content || getConversationSubtitle(conversation)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversationData ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={getConversationAvatar(selectedConversationData)} />
                    <AvatarFallback>
                      {selectedConversationData.type === 'group'
                        ? selectedConversationData.name?.slice(0, 2).toUpperCase()
                        : getConversationName(selectedConversationData)
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h4 className="font-semibold">
                      {getConversationName(selectedConversationData)}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {getConversationSubtitle(selectedConversationData)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {selectedConversationData.type === 'direct' && (
                    <>
                      <Button variant="ghost" size="sm">
                        <Phone className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Video className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="gap-2">
                        <Pin className="w-4 h-4" />
                        {selectedConversationData.isPinned ? 'Unpin' : 'Pin'} Conversation
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-2">
                        <Archive className="w-4 h-4" />
                        Archive Conversation
                      </DropdownMenuItem>
                      <DropdownMenuItem className="gap-2">
                        <Users className="w-4 h-4" />
                        View Participants
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {conversationMessages.map((message) => {
                  const sender = mockUsers.find((u) => u.id === message.senderId);
                  const isCurrentUser = message.senderId === currentUserId;

                  return (
                    <div
                      key={message.id}
                      className={cn(
                        'flex items-start gap-3',
                        isCurrentUser ? 'justify-end' : 'justify-start',
                      )}
                    >
                      {!isCurrentUser && (
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={sender?.avatarUrl} />
                          <AvatarFallback className="text-xs">
                            {sender ? `${sender.firstName[0]}${sender.lastName[0]}` : '??'}
                          </AvatarFallback>
                        </Avatar>
                      )}

                      <div
                        className={cn(
                          'max-w-[70%] space-y-1',
                          isCurrentUser ? 'items-end' : 'items-start',
                        )}
                      >
                        {!isCurrentUser && selectedConversationData.type === 'group' && (
                          <p className="text-xs font-medium text-muted-foreground">
                            {sender ? `${sender.firstName} ${sender.lastName}` : 'Unknown'}
                          </p>
                        )}

                        <div
                          className={cn(
                            'rounded-lg px-3 py-2',
                            isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
                          )}
                        >
                          <p className="text-sm">{message.content}</p>
                        </div>

                        <p className="text-xs text-muted-foreground">
                          {formatTime(message.timestamp)}
                          {message.isEdited && ' (edited)'}
                        </p>
                      </div>

                      {isCurrentUser && (
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={sender?.avatarUrl} />
                          <AvatarFallback className="text-xs">
                            {sender ? `${sender.firstName[0]}${sender.lastName[0]}` : '??'}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Message Input */}
            <div className="p-4 border-t border-border">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm">
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Input
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1"
                />
                <Button variant="ghost" size="sm">
                  <Smile className="w-4 h-4" />
                </Button>
                <Button onClick={handleSendMessage} disabled={!messageInput.trim()} size="sm">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No conversation selected</h3>
              <p>Choose a conversation from the list to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
